// The map screen (gate D2.2, decision 0048): a Preact screen on the UI's stack, over the paused game. The map panel
// (@rpg/ui's `MapPanel`: the valley's picture under the fog of what isn't explored yet, the places found, the
// objective and the Wanderer) pans and zooms on the keyboard (arrows on the focused map, + and −), the pad (right
// stick, shoulders), the mouse and touch (drag, wheel, pinch); beside it, how much is found and explored, and the fast
// travel list: one button per reached waypoint. A refused travel says why on the status line.
import { Button, Group, h, IconButton, MapPanel, Menu, Screen, Status, type MapLayers, type MapFog, type MapMarker, type MapPanelHandle, type ScreenContext, type ScreenRender } from "@rpg/ui";
import type { MapContent, PoiInfo } from "./content";

/** What the map screen reads and does. */
export interface MapScreenDeps {
  layers: () => MapLayers;
  fog: () => MapFog;
  map: () => MapContent;
  /** The places found (and how), the Wanderer's place and heading, the objective's place. */
  markers: () => MapMarker[];
  hero: () => { x: number; z: number };
  found: () => { found: number; total: number; explored: number };
  /** The reached waypoints, in the table's order. */
  waypoints: () => PoiInfo[];
  /** Ask the sim to travel; returns the refusal's string id, or null when the Wanderer went. */
  travel: (to: string) => string | null;
  /** Close the map (and resume play when it was opened from play). */
  close: () => void;
  /** The panel's handle, for the game's handle and specs. */
  bind: (h: MapPanelHandle | null) => void;
}

const CSS = `
.rpg-screen[data-screen=map]{width:min(1180px,100%);height:100%;display:flex;flex-direction:column}
.gv-map-body{display:flex;gap:1em;flex:1 1 auto;min-height:0}
.gv-map-body canvas.rpg-map{flex:1 1 auto;min-width:0;width:100%;height:100%;min-height:200px;border-radius:10px;background:#1d2a1f;touch-action:none;cursor:grab}
.gv-map-side{flex:0 0 min(17em,36%);display:flex;flex-direction:column;gap:.5em;overflow:auto}
.gv-map-side h2{margin-top:.3em}
.gv-map-zoom{display:flex;gap:.4em}
.gv-map-zoom .rpg-btn{flex:1 1 auto}
.gv-map-stat{margin:.1em 0;color:var(--rpg-muted)}
`;

/** The map screen's render function. */
export function mapScreen(d: MapScreenDeps): ScreenRender {
  if (typeof document !== "undefined" && !document.getElementById("gv-map-css")) {
    const s = document.createElement("style");
    s.id = "gv-map-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
  let status = "";
  let panel: MapPanelHandle | null = null;
  return (ctx: ScreenContext) => {
    const t = ctx.t;
    const f = d.found();
    const way = d.waypoints();
    const hero = d.hero();
    const m = d.map();
    const go = (p: PoiInfo): void => {
      const refused = d.travel(p.id);
      if (refused) {
        status = t(refused);
        ctx.ui.refresh();
      } else {
        status = "";
        d.close();
      }
    };
    return h(
      Screen,
      { ctx, title: t("map.title"), dim: true, onBack: () => d.close() },
      h(
        "div",
        { class: "gv-map-body" },
        h(MapPanel, {
          layers: d.layers(),
          fog: d.fog(),
          fogColour: m.row.fog as [number, number, number, number],
          markers: d.markers,
          start: { x: hero.x, z: hero.z, scale: m.row.screen.start },
          minScale: m.row.screen.min,
          maxScale: m.row.screen.max,
          label: t("map.label"),
          bind: (hd) => {
            panel = hd;
            d.bind(hd);
          },
        }),
        h(
          "div",
          { class: "gv-map-side" },
          h("p", { class: "gv-map-stat" }, t("map.found", { found: f.found, total: f.total })),
          h("p", { class: "gv-map-stat" }, t("map.explored", { percent: Math.round(f.explored * 100) })),
          h(
            "div",
            { class: "gv-map-zoom", "data-nav-row": "" },
            h(IconButton, { icon: "minus", label: t("map.zoomOut"), onPress: () => panel?.zoom(0.8), action: "zoom-out" }),
            h(Button, { label: t("map.centre"), onPress: () => panel?.centre(d.hero().x, d.hero().z) }),
            h(IconButton, { icon: "plus", label: t("map.zoomIn"), onPress: () => panel?.zoom(1.25), action: "zoom-in" }),
          ),
          h(
            Group,
            { id: "map-travel", title: t("map.travelHeading") },
            way.length
              ? h(
                  Menu,
                  { label: t("map.travelHeading") },
                  way.map((p, i) => h(Button, { label: t("map.travel", { place: t(p.name) }), onPress: () => go(p), ...(i === 0 ? { autofocus: true } : {}) })),
                )
              : h("p", { class: "rpg-hint" }, t("map.none")),
          ),
          h(Status, { id: "map-status", text: status }),
        ),
      ),
    );
  };
}
